import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ApiService } from '../api/api.service';
import { BaseApiResponse } from '../../models/api/api.model';
import { CalculateAssessmentsScoresRequest } from '../../models/assessment/assessment.model';
import {
  CreateWoundResponse,
  GetListWoundByPatientIdResponse,
  WoundListResponse,
  GetWoundByIdResponse,
  WoundForApproveDto,
  WoundApprovalItemDto,
  GetWoundHistoryResponseItem,
} from 'app/core/models/wound/wound.model';
import {
  WoundConsultation,
  WoundConsultationDetail,
} from 'app/core/models/wound/wound-consultation.model';
import {
  CreateWoundModelNoteRequest,
  CreateWoundModelNoteResponse,
} from 'app/core/models/wound/wound-model-note.model';

@Injectable({
  providedIn: 'root',
})
export class WoundService {
  constructor(private apiService: ApiService) {}

  private apiURL = `${environment.apiLocations.patient}/wound`;

  getListWound(
    pageIndex: number,
    pageSize: number,
    filters?: {
      woundStatus?: number | null;
      consultationStatus?: number | null;
      woundType?: number | null;
      woundCategoryType?: number | null;
      name?: string;
      pid?: string;
      startDate?: string | null;
      endDate?: string | null;
      sortBy?: string | null;
      sortDirection?: string | null;
    },
  ): Observable<BaseApiResponse<WoundListResponse>> {
    let url = `${this.apiURL}/getListWound?PageIndex=${pageIndex}&PageSize=${pageSize}`;

    if (filters) {
      if (filters.woundStatus !== null && filters.woundStatus !== undefined) {
        url += `&WoundStatus=${filters.woundStatus}`;
      }
      if (
        filters.consultationStatus !== null &&
        filters.consultationStatus !== undefined
      ) {
        url += `&ConsultationStatus=${filters.consultationStatus}`;
      }
      if (filters.woundType !== null && filters.woundType !== undefined) {
        url += `&WoundType=${filters.woundType}`;
      }
      if (
        filters.woundCategoryType !== null &&
        filters.woundCategoryType !== undefined
      ) {
        url += `&WoundCategoryType=${filters.woundCategoryType}`;
      }
      if (filters.name) {
        url += `&Name=${encodeURIComponent(filters.name)}`;
      }
      if (filters.pid) {
        url += `&PID=${encodeURIComponent(filters.pid)}`;
      }
      if (filters.startDate) {
        const startDate = new Date(filters.startDate);
        startDate.setUTCHours(0, 0, 0, 0);
        url += `&StartCreatedDateTime=${startDate.toISOString()}`;
      }
      if (filters.endDate) {
        const endDate = new Date(filters.endDate);
        endDate.setUTCHours(23, 59, 59, 999);
        url += `&EndCreatedDateTime=${endDate.toISOString()}`;
      }
      if (filters.sortBy) {
        url += `&SortBy=${filters.sortBy}`;
      }
      if (filters.sortDirection) {
        url += `&SortDirection=${filters.sortDirection}`;
      }
    }

    return this.apiService.get<WoundListResponse>(url);
  }

  createWound(body: {
    calculateAssessmentsScores: CalculateAssessmentsScoresRequest[];
    createWoundDto: any;
  }): Observable<BaseApiResponse<CreateWoundResponse>> {
    const url = `${this.apiURL}/createWound`;
    return this.apiService.post<CreateWoundResponse>(url, body);
  }

  assignWoundToDoctor(body: {
    woundId: string;
    isNeedApprove: boolean;
    doctorId: string | null;
    doctorName: string | null;
  }): Observable<BaseApiResponse<any>> {
    const url = `${this.apiURL}/assignWoundToDoctor`;
    return this.apiService.post<any>(url, body);
  }

  getListWoundByPatientId(
    patientId: string,
  ): Observable<BaseApiResponse<GetListWoundByPatientIdResponse[]>> {
    const url = `${this.apiURL}/getListWoundByPatientId?Id=${patientId}`;
    return this.apiService.get<GetListWoundByPatientIdResponse[]>(url);
  }

  getWoundById(
    woundId: string,
  ): Observable<BaseApiResponse<GetWoundByIdResponse>> {
    const url = `${this.apiURL}/getWoundById?Id=${woundId}`;
    return this.apiService.get<GetWoundByIdResponse>(url);
  }

  createWoundConsultation(patientId: string, message: string, files?: File[]) {
    const url = `${this.apiURL}/createWoundConsultation`;

    const formData = new FormData();
    formData.append('patientId', patientId);
    formData.append('message', message);

    if (files && files.length > 0) {
      files.forEach((file) => {
        formData.append('files', file);
      });
    }

    return this.apiService.post(url, formData);
  }

  getListWoundConsultation(
    pageIndex: number,
    pageSize: number,
    sortBy?: string | null,
    sortDirection?: string | null,
  ): Observable<BaseApiResponse<WoundConsultation>> {
    let url = `${this.apiURL}/getListWoundConsultation?pageIndex=${pageIndex}&pageSize=${pageSize}`;
    if (sortBy) {
      url += `&SortBy=${sortBy}`;
    }
    if (sortDirection) {
      url += `&SortDirection=${sortDirection}`;
    }
    return this.apiService.get<WoundConsultation>(url);
  }

  assignWoundConsultationToDoctor(body: {
    consultationId: string;
    isNeedApprove: boolean;
    doctorId: string | null;
    doctorName: string | null;
  }): Observable<BaseApiResponse<any>> {
    const url = `${this.apiURL}/assignWoundConsultationToDoctor`;
    return this.apiService.post<any>(url, body);
  }

  getWoundConsultationById(
    id: string,
  ): Observable<BaseApiResponse<WoundConsultationDetail>> {
    const url = `${this.apiURL}/getWoundConsultationById?id=${id}`;
    return this.apiService.get<WoundConsultationDetail>(url);
  }

  updateWoundConsultation(body: {
    id: string;
    notes: string | null;
    hasWound: boolean;
  }) {
    const url = `${this.apiURL}/updateWoundConsultation`;
    return this.apiService.post<WoundConsultationDetail>(url, body);
  }

  answerWoundConsultationHasWound(body: {
    id: string;
    hasWound: boolean;
  }) {
    const url = `${this.apiURL}/answerWoundConsultationHasWound`;
    return this.apiService.post<WoundConsultationDetail>(url, body);
  }

  archiveWound(woundId: string): Observable<BaseApiResponse<any>> {
    const url = `${this.apiURL}/archiveWound?WoundId=${woundId}`;
    return this.apiService.post<any>(url, {});
  }

  unarchiveWound(woundId: string): Observable<BaseApiResponse<any>> {
    const url = `${this.apiURL}/unarchiveWound?WoundId=${woundId}`;
    return this.apiService.post<any>(url, {});
  }

  getWoundForApproveByWoundId(
    woundId: string,
  ): Observable<BaseApiResponse<WoundApprovalItemDto>> {
    const url = `${this.apiURL}/getWoundForApproveByWoundId?WoundId=${woundId}`;
    return this.apiService.get<WoundApprovalItemDto>(url);
  }

  approveWound(body: {
    woundIds: string[];
    doctorId: string;
    doctorName: string;
  }) {
    const url = `${this.apiURL}/approveWound`;
    return this.apiService.post<WoundApprovalItemDto>(url, body);
  }

  approveWoundConsultation(body: {
    consultationIds: string[];
    doctorId: string;
    doctorName: string;
  }) {
    const url = `${this.apiURL}/approveWoundConsultation`;
    return this.apiService.post(url, body);
  }

  createWoundModelNote(
    body: CreateWoundModelNoteRequest,
  ): Observable<BaseApiResponse<CreateWoundModelNoteResponse>> {
    const url = `${this.apiURL}/createWoundModelNote`;
    return this.apiService.post<CreateWoundModelNoteResponse>(url, body);
  }

  getWoundModelNotesByWoundIds(woundIds: string[]) {
    const url = `${this.apiURL}/getWoundModelNotesByWoundIds`;
    return this.apiService.post(url, {
      woundIds: woundIds,
    });
  }

  deleteWoundModelNote(id: string): Observable<BaseApiResponse<any>> {
    const url = `${this.apiURL}/deleteWoundModelNote`;
    return this.apiService.post<any>(url, {
      id: id,
    });
  }

  updateWoundModelNote(body: {
    id: string;
    note: string;
  }): Observable<BaseApiResponse<any>> {
    const url = `${this.apiURL}/updateWoundModelNote`;
    return this.apiService.post<any>(url, body);
  }

  calculateAssessmentsScores(body: {
    calculateAssessmentsScores: CalculateAssessmentsScoresRequest[];
    patientId: string | null;
  }): Observable<BaseApiResponse<any>> {
    const url = `${environment.apiLocations.patient}/assessment/calculateAssessmentsScores`;
    return this.apiService.post<any>(url, body);
  }

  getWoundHistory(
    woundId: string,
  ): Observable<BaseApiResponse<GetWoundHistoryResponseItem[]>> {
    const url = `${this.apiURL}/getWoundHistory?WoundId=${woundId}`;
    return this.apiService.get<GetWoundHistoryResponseItem[]>(url);
  }
}
